// MCP detail with tabs: Overview, Tools, Permissions matrix, Test tool.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Play, Save, Trash2, Wifi } from 'lucide-react';
import type {
  CallToolResult,
  McpConnectionDetail,
  McpPermissionRow,
  ToolPermission,
} from '@qualibot/shared';
import { Card } from '@/components/primitives/Card';
import { Badge, type BadgeTone } from '@/components/primitives/Badge';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { Btn } from '@/components/primitives/Btn';
import { Empty } from '@/components/primitives/Empty';
import { Tabs } from '@/components/primitives/Tabs';
import { PageContainer, PageHeader, SectionLabel } from '@/components/PageHeader';
import {
  applyMcpPermissions,
  callMcpTool,
  deleteMcpConnection,
  fetchMcpConnection,
  fetchMcpPermissions,
  testMcpConnection,
} from '@/lib/mcps-api';
import { extractApiError } from '@/lib/api-client';
import { humanizeEnum } from '@/lib/format';

const RISK_TONE: Record<string, BadgeTone> = {
  LOW: 'ok',
  MEDIUM: 'info',
  HIGH: 'warn',
  CRITICAL: 'err',
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'tools', label: 'Tools' },
  { id: 'permissions', label: 'Permissions' },
  { id: 'test', label: 'Test tool' },
] as const;

export function McpDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<(typeof TABS)[number]['id']>('overview');

  const detail = useQuery({
    queryKey: ['mcp', id],
    queryFn: () => fetchMcpConnection(id as string),
    enabled: !!id,
  });

  if (detail.isLoading || !detail.data) {
    return (
      <PageContainer>
        <div className="text-[13px] text-text-3 py-10 text-center">Loading MCP…</div>
      </PageContainer>
    );
  }
  if (detail.isError) {
    return (
      <PageContainer>
        <Card>
          <Empty title="Couldn't load MCP" actions={<Btn onClick={() => detail.refetch()}>Retry</Btn>} />
        </Card>
      </PageContainer>
    );
  }

  const mcp = detail.data;

  return (
    <PageContainer>
      <PageHeader
        title={mcp.name}
        description={`${humanizeEnum(mcp.category)} · ${mcp.endpointUrl}`}
        actions={
          <>
            <Btn icon={ArrowLeft} onClick={() => navigate('/mcp-tools')}>
              Back
            </Btn>
            <DeleteButton
              id={mcp.id}
              onDeleted={() => {
                queryClient.invalidateQueries({ queryKey: ['mcps'] });
                navigate('/mcp-tools');
              }}
            />
          </>
        }
      />

      <div className="flex items-center gap-2 mb-4">
        <StatusBadge status={mcp.status} />
        {mcp.uptime && <Badge>Uptime {mcp.uptime}</Badge>}
        {mcp.isMock && <Badge tone="info">Mock</Badge>}
        <Badge>{mcp.tools.length} tools</Badge>
      </div>

      <Tabs tabs={[...TABS]} active={tab} onChange={(t) => setTab(t as typeof tab)} />

      {tab === 'overview' && <OverviewTab mcp={mcp} />}
      {tab === 'tools' && <ToolsTab tools={mcp.tools} />}
      {tab === 'permissions' && <PermissionsTab mcpId={mcp.id} />}
      {tab === 'test' && <TestToolTab mcp={mcp} />}
    </PageContainer>
  );
}

// ===== Overview =====

function OverviewTab({ mcp }: { mcp: McpConnectionDetail }) {
  const test = useMutation({
    mutationFn: () => testMcpConnection(mcp.id),
  });

  return (
    <div className="grid grid-cols-3 gap-3.5">
      <Card className="col-span-2">
        <div className="font-semibold text-[13.5px] mb-2">Description</div>
        <div className="text-[13.5px] text-text-2 leading-relaxed mb-4">
          {mcp.description || 'No description provided.'}
        </div>
        <div className="grid grid-cols-2 gap-3.5">
          <Info label="Endpoint" value={mcp.endpointUrl} mono />
          <Info label="Auth" value={mcp.authConfigPreview} mono />
          <Info label="Status" value={mcp.status} />
          <Info label="Uptime" value={mcp.uptime ?? '—'} />
        </div>
      </Card>

      <Card>
        <div className="font-semibold text-[13.5px] mb-3">Used by agents</div>
        {mcp.usedByAgents.length === 0 ? (
          <div className="text-[13px] text-text-3">No agents yet. Wire one via Permissions.</div>
        ) : (
          <div className="flex flex-col gap-2">
            {mcp.usedByAgents.map((a) => (
              <div key={a} className="text-[13px]">
                {a}
              </div>
            ))}
          </div>
        )}

        <SectionLabel>Test connection</SectionLabel>
        <Btn icon={Wifi} onClick={() => test.mutate()} disabled={test.isPending}>
          {test.isPending ? 'Pinging…' : 'Ping MCP'}
        </Btn>
        {test.data && (
          <div
            className="mt-2.5 px-3 py-2 rounded-md text-[12.5px]"
            style={{
              background: test.data.ok ? 'var(--ok-soft)' : 'var(--warn-soft)',
              color: test.data.ok ? 'var(--ok)' : 'var(--warn)',
            }}
          >
            {test.data.message} · {test.data.latencyMs}ms
          </div>
        )}
      </Card>
    </div>
  );
}

function Info({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[11.5px] uppercase tracking-[0.07em] font-semibold text-text-3 mb-1">
        {label}
      </div>
      <div
        className="text-[13px] text-text break-all"
        style={{ fontFamily: mono ? 'var(--font-mono)' : 'var(--font-ui)' }}
      >
        {value}
      </div>
    </div>
  );
}

// ===== Tools =====

function ToolsTab({ tools }: { tools: McpConnectionDetail['tools'] }) {
  if (tools.length === 0) {
    return (
      <Card>
        <Empty title="No tools yet" body="Add tools through the MCP server's discovery endpoint." />
      </Card>
    );
  }
  return (
    <Card pad={0}>
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr>
            <Th>Tool</Th>
            <Th>Purpose</Th>
            <Th>Risk</Th>
            <Th>Status</Th>
            <Th>Requires approval</Th>
          </tr>
        </thead>
        <tbody>
          {tools.map((t) => (
            <tr key={t.id} style={{ borderTop: '1px solid var(--border)' }}>
              <Td>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{t.name}</span>
              </Td>
              <Td>{t.description}</Td>
              <Td>
                <Badge tone={RISK_TONE[t.riskLevel] ?? 'default'}>
                  {t.riskLevel.charAt(0) + t.riskLevel.slice(1).toLowerCase()}
                </Badge>
              </Td>
              <Td>
                <Badge tone={t.enabled ? 'ok' : 'default'}>{t.enabled ? 'Enabled' : 'Disabled'}</Badge>
              </Td>
              <Td>{t.requiresApproval ? 'Yes' : 'No'}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left font-semibold uppercase tracking-[0.06em] text-text-3 text-[11px] py-2.5 px-3.5 border-b border-border">
      {children}
    </th>
  );
}
function Td({ children }: { children: React.ReactNode }) {
  return <td className="py-2.5 px-3.5 align-middle">{children}</td>;
}

// ===== Permissions =====

function PermissionsTab({ mcpId }: { mcpId: string }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['mcp', mcpId, 'permissions'],
    queryFn: () => fetchMcpPermissions(mcpId),
  });

  const [local, setLocal] = useState<McpPermissionRow[] | null>(null);
  useEffect(() => {
    if (query.data) setLocal(query.data);
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () =>
      applyMcpPermissions(mcpId, {
        changes: (local ?? []).map((r) => ({
          agentId: r.agentId,
          toolId: r.toolId,
          permission: r.permission,
          requiresApproval: r.requiresApproval,
        })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp', mcpId] });
      queryClient.invalidateQueries({ queryKey: ['mcps'] });
      queryClient.invalidateQueries({ queryKey: ['mcp', mcpId, 'permissions'] });
    },
  });

  const grouped = useMemo(() => {
    const map = new Map<string, McpPermissionRow[]>();
    for (const r of local ?? []) {
      const arr = map.get(r.agentName) ?? [];
      arr.push(r);
      map.set(r.agentName, arr);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [local]);

  if (query.isLoading || !local) {
    return <div className="text-[13px] text-text-3 py-10 text-center">Loading permissions…</div>;
  }

  function updateRow(agentId: string, toolId: string, patch: Partial<McpPermissionRow>) {
    setLocal((prev) =>
      (prev ?? []).map((r) => (r.agentId === agentId && r.toolId === toolId ? { ...r, ...patch } : r)),
    );
  }

  const dirty =
    JSON.stringify(local) !== JSON.stringify(query.data);

  return (
    <Card pad={0}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <span className="font-semibold text-[13.5px]">Agent · tool permissions</span>
        <Btn
          kind="primary"
          icon={Save}
          disabled={!dirty || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Saving…' : mutation.isSuccess && !dirty ? 'Saved' : 'Save permissions'}
        </Btn>
      </div>

      <div className="p-4 flex flex-col gap-5">
        {grouped.map(([agentName, rows]) => (
          <div key={agentName}>
            <div className="text-[11.5px] uppercase tracking-[0.07em] font-semibold text-text-3 mb-2">
              {agentName}
            </div>
            <div className="flex flex-col gap-1.5">
              {rows.map((r) => (
                <div
                  key={`${r.agentId}::${r.toolId}`}
                  className="grid items-center gap-3 py-1.5"
                  style={{ gridTemplateColumns: '1fr 180px 130px' }}
                >
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.toolName}</span>
                  <select
                    value={r.permission}
                    onChange={(e) =>
                      updateRow(r.agentId, r.toolId, {
                        permission: e.target.value as ToolPermission,
                      })
                    }
                    className="h-8 px-2.5 rounded-md border border-border-strong bg-surface text-[12.5px]"
                  >
                    <option value="ALLOW">Allow</option>
                    <option value="DENY">Deny</option>
                    <option value="APPROVAL_REQUIRED">Approval required</option>
                  </select>
                  <label className="flex items-center gap-2 text-[12.5px] text-text-2">
                    <input
                      type="checkbox"
                      checked={r.requiresApproval}
                      onChange={(e) =>
                        updateRow(r.agentId, r.toolId, { requiresApproval: e.target.checked })
                      }
                      style={{ accentColor: 'var(--accent)' }}
                    />
                    Approve calls
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ===== Test tool =====

function TestToolTab({ mcp }: { mcp: McpConnectionDetail }) {
  const [toolId, setToolId] = useState<string>(mcp.tools[0]?.id ?? '');
  const [input, setInput] = useState<string>('{}');
  const [result, setResult] = useState<CallToolResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      let parsed: Record<string, unknown> = {};
      try {
        parsed = input.trim() ? JSON.parse(input) : {};
      } catch (e) {
        throw new Error(`Input must be valid JSON: ${(e as Error).message}`);
      }
      return callMcpTool(mcp.id, toolId, { input: parsed });
    },
    onSuccess: (r) => {
      setError(null);
      setResult(r);
    },
    onError: (err) => {
      setError(extractApiError(err));
      setResult(null);
    },
  });

  if (!mcp.tools.length) {
    return (
      <Card>
        <Empty title="No tools to test" body="Connect tools first." />
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3.5">
      <Card>
        <div className="font-semibold text-[13.5px] mb-3">Test a tool</div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-semibold text-text-2">Tool</label>
          <select
            value={toolId}
            onChange={(e) => {
              setToolId(e.target.value);
              setResult(null);
              setError(null);
            }}
            className="h-10 px-3 rounded-[10px] border border-border-strong bg-surface text-[13.5px]"
          >
            {mcp.tools.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5 mt-3.5">
          <label className="text-[12px] font-semibold text-text-2">Input (JSON)</label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={8}
            spellCheck={false}
            className="px-3 py-2.5 rounded-[10px] border border-border-strong bg-surface text-[12.5px] outline-none focus:border-accent resize-y min-h-[160px]"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
        </div>
        {error && (
          <div
            className="mt-3 px-3 py-2 rounded-md text-[12.5px]"
            style={{ background: 'var(--err-soft)', color: 'var(--err)' }}
          >
            {error}
          </div>
        )}
        <div className="mt-4">
          <Btn
            kind="primary"
            icon={Play}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Calling…' : 'Call tool'}
          </Btn>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-[13.5px]">Response</div>
          {result && (
            <Badge tone={result.status === 'SUCCESS' ? 'ok' : result.status === 'FAILED' ? 'err' : 'warn'}>
              {result.status} · {result.latencyMs}ms
            </Badge>
          )}
        </div>
        <pre
          className="text-[12px] leading-relaxed p-3 rounded-md overflow-auto"
          style={{
            fontFamily: 'var(--font-mono)',
            background: 'var(--bg-sunken)',
            border: '1px solid var(--border)',
            maxHeight: 380,
          }}
        >
          {result
            ? JSON.stringify(result.output ?? result.errorMessage ?? null, null, 2)
            : 'Call a tool to see the response.'}
        </pre>
      </Card>
    </div>
  );
}

function DeleteButton({ id, onDeleted }: { id: string; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const mutation = useMutation({
    mutationFn: () => deleteMcpConnection(id),
    onSuccess: () => onDeleted(),
  });
  if (!confirming) {
    return (
      <Btn kind="danger" icon={Trash2} onClick={() => setConfirming(true)}>
        Delete
      </Btn>
    );
  }
  return (
    <>
      <Btn onClick={() => setConfirming(false)}>Cancel</Btn>
      <Btn kind="danger" icon={Trash2} onClick={() => mutation.mutate()} disabled={mutation.isPending}>
        {mutation.isPending ? 'Deleting…' : 'Confirm delete'}
      </Btn>
    </>
  );
}
